/**
 * GitHub-style year heatmap of completions. Hand-rolled SVG (371 rects is
 * nothing; canvas would lose crisp hover), one DELEGATED tooltip div driven
 * from data attributes rather than 371 tooltip portals. Levels are quartiles
 * of the year's max, sequential single-hue ramp, monotonic lightness on the
 * dark surface (level 0 is a neutral surface step, not a hue).
 */
import { useMemo, useRef, useState } from "react";
import s from "./dashboard.module.css";

const CELL = 11;
const GAP = 3;
const WEEKS = 53;
const DAY_MS = 86_400_000;

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Heatmap({ data }: { data: { date: string; count: number }[] }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; date: string; count: number } | null>(null);

  const { cells, monthLabels, max } = useMemo(() => {
    const byDate = new Map(data.map((d) => [d.date, d.count]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // grid ends on today's column; rows are Sun..Sat
    const end = today.getTime();
    const start = end - (WEEKS * 7 - (6 - today.getDay()) - 1) * DAY_MS;
    const cells: { x: number; y: number; date: string; count: number }[] = [];
    const monthLabels: { x: number; label: string }[] = [];
    let lastMonth = -1;
    let max = 0;
    for (let t = start, i = 0; t <= end; t += DAY_MS, i++) {
      const d = new Date(t);
      const week = Math.floor(i / 7);
      const date = isoOf(d);
      const count = byDate.get(date) ?? 0;
      if (count > max) max = count;
      cells.push({ x: week * (CELL + GAP), y: d.getDay() * (CELL + GAP), date, count });
      if (d.getDate() <= 7 && d.getMonth() !== lastMonth && d.getDay() === 0) {
        lastMonth = d.getMonth();
        monthLabels.push({ x: week * (CELL + GAP), label: d.toLocaleDateString(undefined, { month: "short" }) });
      }
    }
    return { cells, monthLabels, max };
  }, [data]);

  const level = (count: number): number => {
    if (count <= 0 || max === 0) return 0;
    return Math.min(4, Math.ceil((count / max) * 4));
  };

  const width = WEEKS * (CELL + GAP);
  const height = 7 * (CELL + GAP) + 14;

  return (
    <div ref={wrapRef} className={s.heatWrap}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label="Completions per day over the last year"
        onMouseLeave={() => setTip(null)}
        onMouseOver={(e) => {
          const el = e.target as SVGElement;
          const date = el.getAttribute("data-date");
          if (!date || !wrapRef.current) return;
          const rect = el.getBoundingClientRect();
          const wrap = wrapRef.current.getBoundingClientRect();
          setTip({
            x: rect.left - wrap.left + rect.width / 2,
            y: rect.top - wrap.top,
            date,
            count: Number(el.getAttribute("data-count") ?? 0),
          });
        }}
      >
        {monthLabels.map((m) => (
          <text key={m.x} x={m.x} y={9} className={s.heatMonth}>
            {m.label}
          </text>
        ))}
        <g transform="translate(0, 14)">
          {cells.map((c) => (
            <rect
              key={c.date}
              x={c.x}
              y={c.y}
              width={CELL}
              height={CELL}
              rx={2.5}
              className={[s.heatCell, tip?.date === c.date ? s.heatCellHover : ""].join(" ")}
              fill={`var(--task-heat-${level(c.count)})`}
              data-date={c.date}
              data-count={c.count}
            />
          ))}
        </g>
      </svg>
      {tip ? (
        <div className={s.heatTip} style={{ left: tip.x, top: tip.y }}>
          <strong>{tip.count === 0 ? "No tasks" : `${tip.count} task${tip.count === 1 ? "" : "s"}`}</strong>{" "}
          {new Date(`${tip.date}T00:00:00`).toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}
        </div>
      ) : null}
      <div className={s.heatLegend} aria-hidden>
        <span>Less</span>
        {[0, 1, 2, 3, 4].map((l) => (
          <span key={l} className={s.heatSwatch} style={{ background: `var(--task-heat-${l})` }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}
